import React from 'react';
import { call, put, takeLatest } from 'redux-saga/effects';

import {
  receiveAPIResponse,
  requestAPIFailed,
  requestAPIHoldingData,
} from 'src/redux/api/actions';
import { apiCallAsync } from 'src/redux/api/saga';
import { REPORT_DETAIL_DATA_KEY } from 'src/redux/constants';
import {
  CANCEL_NARRATIVE_EDITING_ACTION,
  CANCEL_NARRATIVE_EDITING_KEY,
  EXTRA_NARRATIVE_GET_DATA_KEY,
  GET_EXTRA_NARRATIVE_ACTION,
  GET_MULTI_NARRATIVE_ACTION,
  GET_NARRATIVE_ACTION,
  HISTORY_NARRATIVE_ACTION,
  HISTORY_NARRATIVE_KEY,
  LOCK_NARRATIVE_ACTION,
  LOCK_NARRATIVE_KEY,
  MULTI_NARRATIVES_LIST,
  NARRATIVE_GET_DATA_KEY,
  REVERT_HISTORY_NARRATIVE_ACTION,
  REVERT_HISTORY_NARRATIVE_KEY,
} from 'src/redux/constants';
import type { ViewAction } from 'src/redux/viewState/types';
import { getActionAPISource } from 'src/utils';
import { showNotification } from 'src/utils';
import {
  CANCEL_MULTINARRATIVE_ACTION,
  cancelMultiNarrativeSuccess,
  FORCE_UNLOCK_MULTINARRATIVE_ACTION,
  LOCK_MULTINARRATIVE_ACTION,
  lockMultiNarrative,
  lockMultiNarrativeSuccess,
  MULTI_NARRATIVE_CANCEL,
  MULTI_NARRATIVE_FORCE_UNLOCK,
  MULTI_NARRATIVE_LOCK,
  MULTI_NARRATIVE_UPDATE,
  MULTI_NARRATIVE_VERSION_RESTORE,
  receiveExtraNarrativeResponse,
  receiveMultiNarrativeResponse,
  receiveNarrativeResponse,
  RESTORE_VERSION_MULTINARRATIVE_ACTION,
  restoreVersionMultiNarrativeSuccess,
  SAVE_MULTINARRATIVE_ACTION,
  saveMultiNarrativeFailed,
  saveMultiNarrativeSuccess,
} from './actions';
import {
  CancelMultiNarrativeAction,
  ForceUnlockMultiNarrativeAction,
  LockMultiNarrativeAction,
  RestoreVersionMultiNarrativeAction,
  SaveMultiNarrativeAction,
} from './types';

export function* LockMultiNarrativeApiCallSaga(
  action: LockMultiNarrativeAction,
) {
  try {
    const url = getActionAPISource(MULTI_NARRATIVE_LOCK).replace(
      'narrative_pk',
      action.narrativeId,
    );

    const response = yield call(apiCallAsync, url, {}, 'post');

    yield put(
      requestAPIHoldingData(REPORT_DETAIL_DATA_KEY, null, {
        url_format: { _id_: action.reportId },
      }),
    );

    yield put(
      lockMultiNarrativeSuccess({
        narrativeId: action.narrativeId,
        editor: response.data.editor,
        content: response.data.content,
      }),
    );
  } catch (e) {
    console.error(e);

    const msg = e.response?.data?.error;
    if (!msg) return;

    showNotification(
      <>
        {msg}
        <br />
        Please refresh the page
      </>,
      'error',
    );
  }
}

export function* CancelMultiNarrativeApiCallSaga(
  action: CancelMultiNarrativeAction,
) {
  try {
    const url = getActionAPISource(MULTI_NARRATIVE_CANCEL).replace(
      'narrative_pk',
      action.narrativeId,
    );

    const response = yield call(apiCallAsync, url, {}, 'put');

    yield put(
      requestAPIHoldingData(REPORT_DETAIL_DATA_KEY, null, {
        url_format: { _id_: action.reportId },
      }),
    );

    yield put(
      cancelMultiNarrativeSuccess({
        value: response.data,
      }),
    );
  } catch (e) {
    console.error(e);

    const msg = e.response?.data?.error;
    if (!msg) return;

    showNotification(
      <>
        {msg}
        <br />
        Please refresh the page
      </>,
      'error',
    );
  }
}

export function* ForceUnlockMultiNarrativeApiCallSaga(
  action: ForceUnlockMultiNarrativeAction,
) {
  try {
    const url = getActionAPISource(MULTI_NARRATIVE_FORCE_UNLOCK).replace(
      'narrative_pk',
      action.narrativeId,
    );

    yield call(apiCallAsync, url, {}, 'post');

    yield put(
      lockMultiNarrative({
        narrativeId: action.narrativeId,
        reportId: action.reportId,
      }),
    );
  } catch (e) {
    console.error(e);

    const msg = e.response?.data?.error;
    if (!msg) return;

    showNotification(
      <>
        {msg}
        <br />
        Please refresh the page
      </>,
      'error',
    );
  }
}

export function* SaveMultiNarrativeApiCallSaga(
  action: SaveMultiNarrativeAction,
) {
  try {
    const { narrativeId, content, wordCount, autoSave } = action;

    const url = getActionAPISource(MULTI_NARRATIVE_UPDATE).replace(
      'narrative_pk',
      narrativeId,
    );

    const params = {
      content,
      word_count: wordCount,
      autosave: autoSave,
    };

    const response = yield call(apiCallAsync, url, params, 'put');

    if (!autoSave) {
      yield put(
        requestAPIHoldingData(REPORT_DETAIL_DATA_KEY, null, {
          url_format: { _id_: action.reportId },
        }),
      );
    }

    yield put(
      saveMultiNarrativeSuccess({
        value: response.data,
        autosave: autoSave,
      }),
    );
  } catch (e) {
    yield put(saveMultiNarrativeFailed());

    console.error(e);

    const msg = e.response?.data?.error;
    if (!msg) return;

    showNotification(
      <>
        {msg}
        <br />
        Please refresh the page
      </>,
      'error',
    );
  }
}

export function* RestoreVersionMultiNarrativeApiCallSaga(
  action: RestoreVersionMultiNarrativeAction,
) {
  try {
    const { narrativeVersionId, content, narrativeId } = action;

    const url = getActionAPISource(MULTI_NARRATIVE_VERSION_RESTORE).replace(
      'narrative_version_pk',
      narrativeVersionId,
    );

    yield call(apiCallAsync, url, {}, 'post');

    yield put(
      restoreVersionMultiNarrativeSuccess({
        content,
        narrativeId,
      }),
    );
  } catch (e) {
    console.error(e);

    const msg = e.response?.data?.error;
    if (!msg) return;

    showNotification(
      <>
        {msg}
        <br />
        Please refresh the page
      </>,
      'error',
    );
  }
}

export default function* narrativeSaga() {
  yield takeLatest(LOCK_MULTINARRATIVE_ACTION, LockMultiNarrativeApiCallSaga);
  yield takeLatest(
    CANCEL_MULTINARRATIVE_ACTION,
    CancelMultiNarrativeApiCallSaga,
  );
  yield takeLatest(
    FORCE_UNLOCK_MULTINARRATIVE_ACTION,
    ForceUnlockMultiNarrativeApiCallSaga,
  );
  yield takeLatest(SAVE_MULTINARRATIVE_ACTION, SaveMultiNarrativeApiCallSaga);
  yield takeLatest(
    RESTORE_VERSION_MULTINARRATIVE_ACTION,
    RestoreVersionMultiNarrativeApiCallSaga,
  );
}

export function* revertNarrativeHistorySaga(action: ViewAction) {
  try {
    let url = getActionAPISource(REVERT_HISTORY_NARRATIVE_KEY);
    url = url.replace(new RegExp('_id_', 'g'), action.value.sectionPk);
    const response = yield call(
      apiCallAsync,
      url,
      { versionPk: action.value.versionPk },
      'post',
    );

    if (response.status === 200) {
      yield put(
        receiveAPIResponse(REVERT_HISTORY_NARRATIVE_KEY, response.data),
      );

      if (action.value.hasOwnProperty('success_actions')) {
        const successLen = action.value.success_actions.length;
        for (let i = 0, len = successLen; i < len; i++) {
          yield put(action.value.success_actions[i]);
        }
      }
    } else {
      throw new Error('API call has failed');
    }
  } catch (e) {
    yield put(requestAPIFailed(action.dataKey, e.toString()));
    console.error(e);
  }
}

export function* watchRevertNarrativeHistorySaga() {
  yield takeLatest(REVERT_HISTORY_NARRATIVE_ACTION, revertNarrativeHistorySaga);
}

export function* getNarrativeHistorySaga(action: ViewAction) {
  try {
    let url = getActionAPISource(HISTORY_NARRATIVE_KEY);
    url = url.replace(new RegExp('_id_', 'g'), action.value);
    const response = yield call(apiCallAsync, url, action.value, 'get');
    if (response.status === 200) {
      yield put(receiveAPIResponse(HISTORY_NARRATIVE_KEY, response.data));
    } else {
      throw new Error('API call has failed');
    }
  } catch (e) {
    yield put(requestAPIFailed(action.dataKey, e.toString()));
    console.error(e);
  }
}

export function* watchGetNarrativeHistorySaga() {
  yield takeLatest(HISTORY_NARRATIVE_ACTION, getNarrativeHistorySaga);
}

export function* getNarrativeSaga(action: ViewAction) {
  try {
    let url = getActionAPISource(NARRATIVE_GET_DATA_KEY);
    for (const [key, value] of Object.entries(action.value.url_format)) {
      url = url.replace(new RegExp(key, 'g'), value);
    }
    const response = yield call(apiCallAsync, url, null, 'get');
    if (response.status === 200) {
      yield put(receiveNarrativeResponse(response.data));
    } else {
      throw new Error('API call getNarrative has failed');
    }
  } catch (e) {
    yield put(requestAPIFailed(action.dataKey, e.toString()));
    console.error(e);
  }
}

export function* getMultiNarrativeSaga(action: ViewAction) {
  try {
    let url = getActionAPISource(MULTI_NARRATIVES_LIST);
    for (const [key, value] of Object.entries(action.value.url_format)) {
      url = url.replace(new RegExp(key, 'g'), value);
    }
    const response = yield call(apiCallAsync, url, null, 'get');
    if (response.status === 200) {
      yield put(receiveMultiNarrativeResponse(response.data));
    } else {
      throw new Error('API call getMultiNarrative has failed');
    }
  } catch (e) {
    yield put(requestAPIFailed(action.dataKey, e.toString()));
    console.error(e);
  }
}

export function* watchMultiNarrativeSaga() {
  yield takeLatest(GET_MULTI_NARRATIVE_ACTION, getMultiNarrativeSaga);
}

export function* watchNarrativeSaga() {
  yield takeLatest(GET_NARRATIVE_ACTION, getNarrativeSaga);
}

export function* getExtraNarrativeSaga(action: ViewAction) {
  try {
    let url = getActionAPISource(EXTRA_NARRATIVE_GET_DATA_KEY);
    for (const [key, value] of Object.entries(action.value.url_format)) {
      url = url.replace(new RegExp(key, 'g'), value);
    }
    const response = yield call(apiCallAsync, url, null, 'get');
    if (response.status === 200) {
      yield put(receiveExtraNarrativeResponse(response.data));
    } else {
      throw new Error('API call getExtraNarrative has failed');
    }
  } catch (e) {
    yield put(requestAPIFailed(action.dataKey, e.toString()));
    console.error(e);
  }
}

export function* watchExtraNarrativeSaga() {
  yield takeLatest(GET_EXTRA_NARRATIVE_ACTION, getExtraNarrativeSaga);
}

export function* cancelNarrativeEditingSaga(action: ViewAction) {
  try {
    let url = getActionAPISource(CANCEL_NARRATIVE_EDITING_KEY);
    url = url.replace(new RegExp('_id_', 'g'), action.value.section);
    const response = yield call(
      apiCallAsync,
      url,
      action.value.section,
      'post',
    );

    if (response.status === 200) {
      yield put(
        receiveAPIResponse(CANCEL_NARRATIVE_EDITING_KEY, response.data),
      );
      if (action.value.hasOwnProperty('success_actions')) {
        const successLen = action.value.success_actions.length;
        for (let i = 0, len = successLen; i < len; i++) {
          yield put(action.value.success_actions[i]);
        }
      }
    } else {
      throw new Error('API call has failed');
    }
  } catch (e) {
    yield put(requestAPIFailed(action.dataKey, e.toString()));
    console.error(e);
  }
}

export function* watchCancelNarrativeEditingSaga() {
  yield takeLatest(CANCEL_NARRATIVE_EDITING_ACTION, cancelNarrativeEditingSaga);
}

export function* lockNarrativeSaga(action: ViewAction) {
  try {
    let url = getActionAPISource(LOCK_NARRATIVE_KEY);
    url = url.replace(new RegExp('_id_', 'g'), action.value.section);
    const response = yield call(
      apiCallAsync,
      url,
      action.value.section,
      'post',
    );

    if (response.status === 200) {
      yield put(receiveAPIResponse(LOCK_NARRATIVE_KEY, response.data));
      if (action.value.hasOwnProperty('success_actions')) {
        const successLen = action.value.success_actions.length;
        for (let i = 0, len = successLen; i < len; i++) {
          yield put(action.value.success_actions[i]);
        }
      }
    } else {
      throw new Error('API call has failed');
    }
  } catch (e) {
    yield put(requestAPIFailed(action.dataKey, e.toString()));
    console.error(e);
    if (action.value.hasOwnProperty('failure_actions')) {
      const failureLen = action.value.failure_actions.length;
      for (let i = 0, len = failureLen; i < len; i++) {
        yield put(action.value.failure_actions[i]);
      }
    }
  }
}

export function* watchLockNarrativeSaga() {
  yield takeLatest(LOCK_NARRATIVE_ACTION, lockNarrativeSaga);
}
